import { FC, createContext, useCallback, useContext, useMemo, useReducer } from 'react';
import { CampaignWorkflowFormState } from '../state/CampaignWorkflowFormState';
import { CampaignWorkflowFormActions, FormKeys } from '../state/CampaignWorkflowFormActions';
import { CampaignWorkflowFormReducer } from '../state/CampaignWorkflowFormReducer';
import { CampaignWorkflowFormInitialState } from '../state/CampaignWorkflowFormInitialState';
import { useGetEditOrActual } from '../state/CampaignSettings/selectors/useGetEditOrActual';

interface CampaignWorkflowFormContext {
  state: CampaignWorkflowFormState;
  actions: ReturnType<typeof CampaignWorkflowFormActions>;
  formDisabled: boolean;
  getIsTouched: (key: FormKeys | string) => boolean;
  getIsFocused: (key: FormKeys | string) => boolean;
}

const Context = createContext<CampaignWorkflowFormContext>(null);

export const CampaignWorkflowFormProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(CampaignWorkflowFormReducer, CampaignWorkflowFormInitialState);

  const editorState = useGetEditOrActual();
  const formDisabled = useMemo(() => editorState.status === 'Test' || editorState.status === 'Live', [
    editorState.status,
  ]);

  const getIsTouched = useCallback(
    (key: string) => {
      return state.touched[key];
    },
    [state.touched],
  );

  const getIsFocused = useCallback(
    (key: string) => {
      return state.focused[key];
    },
    [state.focused],
  );

  const value: CampaignWorkflowFormContext = useMemo(() => {
    return {
      state,
      actions: CampaignWorkflowFormActions(dispatch),
      formDisabled,
      getIsTouched,
      getIsFocused,
    };
  }, [formDisabled, getIsFocused, getIsTouched, state]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useWorkflowForm = () => {
  const contextValues = useContext(Context);
  if (!contextValues) {
    throw new Error('useWorkflowForm must be used in a CampaignWorkflowFormProvider');
  }

  return contextValues;
};
